/**
 * Real-time metrics widget — near-live activity from MetricsHub, with an HTTP
 * snapshot fallback when SignalR is offline so the panel still shows numbers.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Chip } from '@coordio/ui';
import { DescriptionList, EmptyState, FormSection } from '@coordio/ui/layout';

import type { RealTimeMetricsDto } from '../../types/dashboard';
import { dashboardService } from '../../services/dashboardService';
import { metricsSignalRService } from '../../services/metricsSignalRService';
import { useAuthStore } from '../../store/authStore';
import { useContextStore } from '../../store/contextStore';
import { useSignalR } from '../../providers/SignalRProvider';

import './dashboardWidgets.scss';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 6 }).format(value);

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const STATUS_CHIP = {
  connected: { label: 'Live', color: 'green' },
  connecting: { label: 'Connecting', color: 'amber' },
  disconnected: { label: 'Offline', color: 'default' },
} as const;

type RealTimeMetricsProps = {
  /** When true, skip FormSection chrome (used inside customize canvas cards). */
  embedded?: boolean;
};

export const RealTimeMetrics = ({ embedded = false }: RealTimeMetricsProps) => {
  const { metricsStatus } = useSignalR();
  const [liveMetrics, setLiveMetrics] = useState<RealTimeMetricsDto | null>(null);
  const token = useAuthStore((s) => s.token);
  const tenantId = useContextStore((s) => s.currentTenant?.id ?? '');
  const projectId = useContextStore((s) => s.currentProject?.id);

  useEffect(() => {
    metricsSignalRService.on({
      onRealTimeMetricsUpdated: (data) => {
        setLiveMetrics(data);
      },
    });

    return () => {
      metricsSignalRService.off();
    };
  }, []);

  const needsHttpFallback = metricsStatus !== 'connected' && !!token && !!tenantId;

  const { data: httpMetrics, isLoading: httpLoading } = useQuery({
    queryKey: ['dashboard', 'realTimeMetrics', tenantId, projectId],
    queryFn: () => dashboardService.getRealTimeMetrics(),
    enabled: needsHttpFallback,
    refetchInterval: needsHttpFallback ? 15_000 : false,
  });

  const metrics = liveMetrics ?? httpMetrics ?? null;
  const chip = STATUS_CHIP[metricsStatus];

  const body = (
    <>
      <div className="realtime-metrics__status">
        <Chip
          label={liveMetrics ? chip.label : needsHttpFallback && metrics ? 'Snapshot' : chip.label}
          color={liveMetrics ? chip.color : needsHttpFallback && metrics ? 'amber' : chip.color}
          data-testid="realtime-status"
        />
      </div>

      {!token && <EmptyState title="Not signed in" description="Sign in to enable real-time metrics." />}

      {token && !tenantId && (
        <EmptyState
          title="No workspace selected"
          description="Select a workspace (and project) to enable real-time metrics."
        />
      )}

      {tenantId && !metrics && httpLoading && <p className="realtime-metrics__waiting">Loading snapshot…</p>}

      {tenantId && !metrics && !httpLoading && (
        <p className="realtime-metrics__waiting">Waiting for updates…</p>
      )}

      {metrics && (
        <DescriptionList
          compact
          items={[
            { key: 'active-sessions', label: 'Active sessions', value: formatNumber(metrics.activeSessions) },
            { key: 'executions', label: 'Executions (last minute)', value: formatNumber(metrics.executionsLastMinute) },
            { key: 'tokens', label: 'Tokens (last minute)', value: formatNumber(metrics.tokensLastMinute) },
            { key: 'cost', label: 'Cost (last minute)', value: formatCurrency(metrics.costLastMinute) },
          ]}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="realtime-metrics realtime-metrics--embedded" data-testid="realtime-metrics">
        {body}
      </div>
    );
  }

  return (
    <FormSection title="Real-time activity" description="Pushed from the metrics hub as it happens.">
      {body}
    </FormSection>
  );
};
